import type { CalendarEvent, DetectedMeeting, GoogleMeetMeeting, RecallDetectedMeeting } from '@highlight/common'
import { findMatchingCalendarEvent } from '@shared/utils/calendar-matching'
import { getMeetingCalendarEventId, isScheduledMeeting } from './AudioNoteUtils'

type AnyMeeting = DetectedMeeting | GoogleMeetMeeting | RecallDetectedMeeting

/** The meeting's own conference URL, if it has one. Scheduled meetings carry a
 *  `hangoutLink` instead, but they never reach the URL paths below — their id
 *  IS the calendar event id, so they always resolve on the stored link. */
const meetingUrl = (meeting: AnyMeeting): string | undefined => ('url' in meeting ? meeting.url : undefined)

/**
 * Whether the two matchers below may use a title-containment fallback.
 *
 * Main answers "which event did this meeting turn out to be?" and matches on
 * url + title, mirroring the detection-time policy in
 * `matchRecallMeetingToCalendarEvent` — a decision it commits by writing
 * `calendarEventId` and PATCHing the audio session.
 *
 * The renderer answers "is the recording in progress THIS card's event?" for
 * one candidate at a time. Title is too weak there: against a single-element
 * array a loose containment match would light up the wrong card whenever two
 * similarly-named meetings overlap, so it stays URL-precise and lets the
 * stored link (once main resolves it) carry the harder cases.
 *
 * The asymmetry is deliberate; this is the only place it should be described.
 */
type MatchPolicy = 'url-only' | 'url-and-title'

const matchEvents = (
  meeting: AnyMeeting,
  events: Array<CalendarEvent> | undefined,
  policy: MatchPolicy,
  url: string | null | undefined,
): CalendarEvent | null => {
  const title = policy === 'url-and-title' && 'windowTitle' in meeting ? meeting.windowTitle : undefined
  if (!url && !title) return null
  return findMatchingCalendarEvent({ url: url ?? undefined, title }, events)
}

/**
 * Single answer to "does this meeting belong to this calendar event?" — the
 * question the sidepanel asks in half a dozen places to decide between
 * the join CTA, "Transcribe", and "View Transcript".
 *
 * Two layers, because the stored link is not reliable on its own:
 *
 *  1. `calendarEventId`, when the producer managed to resolve one. Authoritative
 *     in both directions — a meeting linked to a different event is not this one,
 *     even if the URLs happen to coincide (a personal Meet room reused
 *     back-to-back).
 *  2. A URL match, when it didn't. `calendarEventId` is assigned once at
 *     detection time and can legitimately come back empty — today's events
 *     hadn't loaded, or Recall reported the window before its URL resolved.
 *     Main repairs the stored value once the inputs arrive, but the UI must not
 *     be wrong in the window before that lands.
 */
export const meetingMatchesCalendarEvent = (
  meeting: AnyMeeting | null | undefined,
  event: CalendarEvent | null | undefined,
): boolean => {
  if (!meeting || !event) return false

  const storedId = getMeetingCalendarEventId(meeting)
  if (storedId) return storedId === event.id

  return matchEvents(meeting, [event], 'url-only', meetingUrl(meeting)) !== null
}

/** The calendar event already attached to a meeting. Only `RecallDetectedMeeting`
 *  declares the field, but a backfill can leave any detected meeting holding an
 *  id with no event, so this is a runtime check. */
const attachedEvent = (meeting: AnyMeeting): CalendarEvent | undefined =>
  'calendarEvent' in meeting ? meeting.calendarEvent : undefined

/**
 * The calendar event to attach to a meeting, or null when there is nothing to
 * do — it is already fully linked, or nothing matches.
 *
 * "Fully linked" means the event itself is attached, not merely its id. The
 * awaited-join backfill copies the id the user picked when they hit the join
 * CTA and nothing else, which leaves
 * `linkCalendarEventToActiveSession` without the `calendarId` it reads off the
 * nested event — so the audio session is never associated server-side and the
 * note cannot inherit the calendar title. Treating a bare id as "done" would
 * make that permanent.
 *
 * A stored id is looked up rather than re-matched: an explicit link is
 * authoritative and must never be second-guessed by url or title.
 *
 * Pure: main owns the side effects (writing the link onto state, PATCHing the
 * audio session) and the URL normalization — hence `url` is a parameter rather
 * than read off the meeting. Main's `getMeetingUrl` collapses browser-internal
 * URLs (about:blank while screen sharing) to null, which is what lets a late
 * URL from Recall be substituted; reading `meeting.url` here would see a
 * truthy "about:blank" and never fall back.
 */
export const resolveMeetingCalendarEvent = (
  meeting: AnyMeeting | null | undefined,
  events: Array<CalendarEvent> | undefined,
  url: string | null | undefined,
): CalendarEvent | null => {
  // A scheduled meeting IS the calendar event — nothing to attach to it.
  if (!meeting || isScheduledMeeting(meeting)) return null
  if (attachedEvent(meeting)) return null

  if (meeting.calendarEventId) return calendarEventForMeeting(meeting, events)

  return matchEvents(meeting, events, 'url-and-title', url)
}

/**
 * The calendar event a meeting is linked to — the one already attached to it,
 * or the one its id points at.
 *
 * This is what the server-side association actually needs: both the
 * audio-session POST and its PATCH send `calendarEventId` + `calendarId`, and
 * those are just `event.id` and `event.calendarId`. Reading them off the
 * meeting instead is what let three shapes drift apart:
 *
 *  - `RecallDetectedMeeting` carries the nested event, so it worked;
 *  - a backfilled meeting carries only the id, so `calendarId` came back
 *    undefined and the association was skipped;
 *  - `GoogleMeetMeeting` (the "Transcribe" CTA on a meeting with no join link)
 *    carries neither field — only its own id, which IS the event id — so it
 *    could never be associated at all.
 *
 * Returns null when the meeting has no link, or when its event is not among
 * `events` (an out-of-window occurrence). Callers must still check
 * `calendarId`, which the backend requires and some providers omit.
 */
export const calendarEventForMeeting = (
  meeting: AnyMeeting | null | undefined,
  events: Array<CalendarEvent> | undefined,
): CalendarEvent | null => {
  if (!meeting) return null

  const attached = attachedEvent(meeting)
  if (attached) return attached

  const id = getMeetingCalendarEventId(meeting)
  if (!id) return null
  return events?.find((event) => event.id === id) ?? null
}
